<template>
  <div class="otp-input-group" style="display: flex; align-items: center;">
    <span v-for="(digit, i) in numInputs" :key="i" style="display: contents">
      <input
        :ref="`otp-${i}`"
        type="tel"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="one-time-code"
        :class="inputClasses"
        :value="otp[i] || ''"
        @input="handleInput(i, $event)"
        @keydown="handleKeyDown(i, $event)"
        @paste="handlePaste(i, $event)"
        @focus="handleFocus(i)"
      />
      <span v-if="i < numInputs - 1 && separator" v-html="separator"></span>
    </span>
  </div>
</template>

<script>
// Lightweight replacement for @bachdgvn/vue-otp-input.
//
// The original library sets maxlength="1" on each box and truncates any
// value longer than one character. That breaks native OS/keyboard OTP
// autofill (iOS QuickType bar / Android SMS autofill), which inserts the
// *entire* code into whichever box currently has focus: the extra digits
// were silently dropped, so tapping the suggestion appeared to do nothing.
//
// This component keeps each box's DOM value fully controlled by Vue (no
// maxlength attribute), reads whatever text lands in the input on the
// native "input"/"paste" events - one digit or the whole code - and
// distributes it across the boxes starting at the box that received it.
const BACKSPACE = 8;
const DELETE = 46;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;

export default {
  name: "OtpInput",
  props: {
    numInputs: {
      type: Number,
      default: 4
    },
    separator: {
      type: String,
      default: ""
    },
    inputClasses: {
      type: String,
      default: ""
    },
    shouldAutoFocus: {
      type: Boolean,
      default: false
    },
    // Kept for backwards compatibility with existing usages; input is
    // always restricted to digits in this component.
    isInputNum: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      otp: Array(this.numInputs).fill("")
    };
  },
  mounted() {
    if (this.shouldAutoFocus) {
      this.focusInput(0);
    }
  },
  methods: {
    focusInput(index) {
      const clamped = Math.max(0, Math.min(this.numInputs - 1, index));
      const el = this.$refs[`otp-${clamped}`];
      const input = Array.isArray(el) ? el[0] : el;
      if (input) {
        this.$nextTick(() => {
          input.focus();
          input.select();
        });
      }
    },
    handleFocus(index) {
      const el = this.$refs[`otp-${index}`];
      const input = Array.isArray(el) ? el[0] : el;
      if (input) input.select();
    },
    // Handles both a single typed digit AND a full code inserted at once
    // by OS-level OTP autofill (or a fast on-screen keyboard).
    handleInput(index, event) {
      const rawValue = event.target.value || "";
      const digits = rawValue.replace(/\D/g, "");

      if (digits.length <= 1) {
        this.$set(this.otp, index, digits);
        this.emitChange();
        if (digits) {
          this.focusInput(index + 1);
        }
        return;
      }

      // Multiple digits landed in one box: autofill or a paste-like insert.
      this.distribute(index, digits);
    },
    handlePaste(index, event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData(
        "text"
      );
      this.distribute(index, text);
    },
    distribute(startIndex, text) {
      const digits = (text || "").replace(/\D/g, "").split("");
      if (!digits.length) return;

      const next = this.otp.slice();
      let cursor = startIndex;
      digits.slice(0, this.numInputs - startIndex).forEach(d => {
        next[cursor] = d;
        cursor += 1;
      });
      this.otp = next;
      this.emitChange();

      if (cursor >= this.numInputs) {
        // Fully filled: blur the last box to dismiss the mobile keyboard.
        const el = this.$refs[`otp-${this.numInputs - 1}`];
        const input = Array.isArray(el) ? el[0] : el;
        if (input) input.blur();
      } else {
        this.focusInput(cursor);
      }
    },
    handleKeyDown(index, event) {
      // Let control/navigation keys and copy-paste shortcuts through, but
      // reject other non-digit characters before they ever reach the input
      // (desktop keyboards only - mobile/OS autofill never dispatches these).
      const isDigit = event.key && /^[0-9]$/.test(event.key);
      const isNavOrControlKey = [
        BACKSPACE,
        DELETE,
        LEFT_ARROW,
        RIGHT_ARROW,
        9, // Tab
        16, // Shift
        17, // Ctrl
        18, // Alt
        91,
        93 // Meta/Cmd
      ].includes(event.keyCode);
      const isPasteShortcut =
        (event.ctrlKey || event.metaKey) && event.key === "v";
      if (!isDigit && !isNavOrControlKey && !isPasteShortcut) {
        event.preventDefault();
      }

      switch (event.keyCode) {
        case BACKSPACE:
          event.preventDefault();
          if (this.otp[index]) {
            this.$set(this.otp, index, "");
            this.emitChange();
          } else {
            this.$set(this.otp, index, "");
            this.focusInput(index - 1);
          }
          break;
        case DELETE:
          event.preventDefault();
          this.$set(this.otp, index, "");
          this.emitChange();
          break;
        case LEFT_ARROW:
          event.preventDefault();
          this.focusInput(index - 1);
          break;
        case RIGHT_ARROW:
          event.preventDefault();
          this.focusInput(index + 1);
          break;
        default:
          break;
      }
    },
    emitChange() {
      const value = this.otp.join("");
      this.$emit("on-change", value);
      if (value.length === this.numInputs) {
        this.$emit("on-complete", value);
      }
    },
    clearInput() {
      this.otp = Array(this.numInputs).fill("");
      this.focusInput(0);
    }
  }
};
</script>
